import React, { useCallback, useEffect, useMemo, useState } from "react";
import employeesApi from "../../api/employeesApi";
import styles from "./BranchConfigurationPage.module.css";

const PAGE_SIZE = 50;

export default function EmployeeManagerModal({ branch, onClose, onBranchCountChange }) {
  const nomApplications = useMemo(
    () => (branch.surveys || [])
      .filter((survey) => ["nom035", "nom035SecI"].includes(survey.name))
      .flatMap((survey) => (survey.applications || []).map((application) => ({
        ...application,
        typeApplication: survey.name,
      }))),
    [branch.surveys]
  );
  const [scope, setScope] = useState("branch");
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const [message, setMessage] = useState("");

  const isBranch = scope === "branch";
  const applicationId = isBranch ? null : scope.replace(/^nom:/, "");
  const selectedApplication = nomApplications.find(
    (application) => String(application.questionnaireApplicationId) === applicationId
  );
  const answeredField = selectedApplication?.typeApplication === "nom035SecI"
    ? "answeredNom035SecI"
    : "answeredNom";

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const result = isBranch
        ? await employeesApi.getEmployeesByBranch(branch._id, { page, pageSize: PAGE_SIZE })
        : await employeesApi.getNomApplicationEmployees(applicationId, { page, pageSize: PAGE_SIZE });
      setEmployees(result.data || []);
      setTotal(result.total || 0);
      if (isBranch) onBranchCountChange(result.total || 0);
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudieron cargar los empleados.");
    } finally {
      setLoading(false);
    }
  }, [applicationId, branch._id, isBranch, onBranchCountChange, page]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const changeScope = (event) => {
    setScope(event.target.value);
    setPage(1);
    setEditingId(null);
    setMessage("");
  };

  const addEmployee = async (event) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setSaving(true);
    setMessage("");
    try {
      if (isBranch) {
        await employeesApi.createEmployee(branch._id, email);
      } else {
        await employeesApi.createNomApplicationEmployee(applicationId, email);
      }
      setNewEmail("");
      setMessage("Empleado agregado correctamente.");
      await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo agregar el empleado.");
    } finally {
      setSaving(false);
    }
  };

  const saveEmployee = async (employeeId) => {
    const email = editingEmail.trim().toLowerCase();
    if (!email) return;
    setSaving(true);
    setMessage("");
    try {
      if (isBranch) {
        await employeesApi.updateEmployee(employeeId, email);
      } else {
        await employeesApi.updateNomApplicationEmployee(applicationId, employeeId, email);
      }
      setEditingId(null);
      setEditingEmail("");
      setMessage("Empleado actualizado correctamente.");
      await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo actualizar el empleado.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEmployee = async (employee) => {
    if (!window.confirm(`¿Eliminar a ${employee.email}?`)) return;
    setSaving(true);
    setMessage("");
    try {
      if (isBranch) {
        await employeesApi.deleteEmployee(employee._id);
      } else {
        await employeesApi.deleteNomApplicationEmployee(applicationId, employee._id);
      }
      setMessage("Empleado eliminado correctamente.");
      const remainingOnPage = employees.length - 1;
      if (remainingOnPage === 0 && page > 1) setPage((current) => current - 1);
      else await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo eliminar el empleado.");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <div className={styles.modalOverlay} role="presentation" onMouseDown={onClose}>
      <section
        className={styles.employeeModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-manager-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.employeeModalHeader}>
          <div>
            <span>Administración de padrones</span>
            <h2 id="employee-manager-title">Empleados de {branch.name}</h2>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        <label className={styles.managerLabel} htmlFor="employee-scope">Padrón a administrar</label>
        <select id="employee-scope" className={styles.input} value={scope} onChange={changeScope}>
          <option value="branch">Sucursal · padrón general</option>
          {nomApplications.map((application) => (
            <option
              key={application.questionnaireApplicationId}
              value={`nom:${application.questionnaireApplicationId}`}
            >
              {application.typeApplication === "nom035SecI" ? "NOM-035 Guía I" : "NOM-035"} · {application.applicationName}
            </option>
          ))}
        </select>

        {!nomApplications.length && (
          <p className={styles.fieldHelp}>Esta sucursal todavía no tiene aplicaciones NOM-035.</p>
        )}

        <form className={styles.employeeAddForm} onSubmit={addEmployee}>
          <input
            type="email"
            className={styles.input}
            placeholder="empleado@empresa.com"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
          />
          <button type="submit" className={styles.primaryButton} disabled={saving || !newEmail.trim()}>
            Agregar empleado
          </button>
        </form>

        {message && <div className={styles.message}>{message}</div>}

        <div className={styles.employeeTableWrapper}>
          <table className={styles.employeeTable}>
            <thead>
              <tr>
                <th>Correo</th>
                {!isBranch && <th>Estatus NOM</th>}
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isBranch ? 2 : 3}>Cargando empleados...</td></tr>
              ) : employees.length ? employees.map((employee) => (
                <tr key={employee._id}>
                  <td>
                    {editingId === employee._id ? (
                      <input
                        type="email"
                        className={styles.input}
                        value={editingEmail}
                        onChange={(event) => setEditingEmail(event.target.value)}
                      />
                    ) : employee.email}
                  </td>
                  {!isBranch && <td>{employee[answeredField] ? "Respondido" : "Sin responder"}</td>}
                  <td>
                    <div className={styles.employeeActions}>
                      {editingId === employee._id ? (
                        <>
                          <button type="button" onClick={() => saveEmployee(employee._id)} disabled={saving}>Guardar</button>
                          <button type="button" onClick={() => setEditingId(null)}>Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(employee._id);
                              setEditingEmail(employee.email);
                            }}
                          >
                            Editar
                          </button>
                          <button type="button" className={styles.dangerButton} onClick={() => deleteEmployee(employee)} disabled={saving}>
                            Eliminar
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={isBranch ? 2 : 3}>No hay empleados registrados.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.employeePagination}>
          <span>{total} empleados · página {page} de {totalPages}</span>
          <div>
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Anterior</button>
            <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>Siguiente</button>
          </div>
        </div>
      </section>
    </div>
  );
}
